import { useEffect, useRef } from 'react'

import { useAnimationFrame } from '../../../../hooks'
import {
  WYSIWYG_EDITOR_EVENT_REMOVE,
  WYSIWYG_EDITOR_FULL_SCREEN_MIN_HEIGHT,
} from '../../WysiwygEditor.constants'
import {
  getWysiwygEditorFullScreenHeight,
  getWysiwygEditorHeightLimits,
  setWysiwygEditorHeightLimits,
} from '../../WysiwygEditor.utils.height'

export function useWysiwygEditorFullScreenHeight({ isFullScreen }) {
  const contentRef = useRef(null)

  const contentWrapperRef = useRef(null)

  const editorRef = useRef(null)

  const windowedHeightLimitsRef = useRef(null)

  // written to the editor directly: a re-created instance must get unchanged values too
  function handleUpdateFieldHeight() {
    const editor = editorRef.current

    if (editor?.container && contentRef.current && contentWrapperRef.current) {
      const fullScreenHeight = isFullScreen
        ? getWysiwygEditorFullScreenHeight({
            content: contentRef.current,
            editor,
            wrapper: contentWrapperRef.current,
          })
        : 0

      setWysiwygEditorHeightLimits(
        editor,
        fullScreenHeight >= WYSIWYG_EDITOR_FULL_SCREEN_MIN_HEIGHT
          ? { maxHeight: fullScreenHeight, minHeight: fullScreenHeight }
          : windowedHeightLimitsRef.current
      )
    }
  }

  const [scheduleHeightUpdate, cancelHeightUpdate] = useAnimationFrame(
    handleUpdateFieldHeight
  )

  function handleEditorInit(editor) {
    editorRef.current = editor

    // the init params are the windowed limits, read before the first overwrite
    windowedHeightLimitsRef.current = getWysiwygEditorHeightLimits(editor)

    // the field can unmount the editor while the modal stays open (post type switch)
    editor.on(WYSIWYG_EDITOR_EVENT_REMOVE, () => {
      editorRef.current = null
    })

    handleUpdateFieldHeight()
  }

  useEffect(() => {
    handleUpdateFieldHeight()
  }, [isFullScreen])

  useEffect(() => {
    const content = contentRef.current
    const wrapper = contentWrapperRef.current

    if (isFullScreen && content && wrapper) {
      const observer = new ResizeObserver(() => {
        if (wrapper.getBoundingClientRect().height > 0) {
          scheduleHeightUpdate()
        }
      })

      // the scroller follows the viewport height, the wrapper does not
      observer.observe(content)
      observer.observe(wrapper)

      return () => {
        cancelHeightUpdate()
        observer.disconnect()
      }
    }
  }, [isFullScreen])

  return { contentRef, contentWrapperRef, editorRef, handleEditorInit }
}
